import { onKeyDownSubmit } from 'accessibility/eventHandlers'
import classnames from 'classnames'
import { Megaphone } from 'components/lib/icons'
import RecipientAvatar from 'components/RecipientAvatar'
import useActiveAnnouncementCampaign from 'hooks/useActiveAnnouncementCampaign'
import useActiveCampaign from 'hooks/useActiveCampaign'
import { useActiveSenderId } from 'hooks/useActiveSenderId'
import useDisableWidgetIcon from 'hooks/useDisableWidgetIcon'
import useEmbedThemeProperties from 'hooks/useEmbedThemeProperties'
import useForceFocus from 'hooks/useForceFocus'
import useIsBotMessage from 'hooks/useIsBotMessage'
import useLastAgentMessage from 'hooks/useLastAgentMessage'
import useOnHover from 'hooks/useOnHover'
import { useRecipientResolution } from 'hooks/useRecipientResolution'
import useThemeColors from 'hooks/useThemeColors'
import useThemeIcon from 'hooks/useThemeIcon'
import useUnreadMessagesForActiveConversation from 'hooks/useUnreadMessagesForActiveConversation'
import useWidgetAlignment from 'hooks/useWidgetAlignment'
import useWidgetIconShape from 'hooks/useWidgetIconShape'
import { triggerPlaybookClickedAPIEvent } from 'modules/api/events'
import { storeAnnouncementCampaignCompleted } from 'modules/campaigns'
import { toggleChat } from 'modules/view/actions'
import { widgetIconVisible } from 'modules/view/selectors'
import { pathOr, uniq } from 'ramda'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isMobileDevice } from 'shared/src/utils/platform'
import { CampaignDisplayType } from 'types/campaigns'
import { GDPRState } from 'types/session'
import { broadcast, listen } from 'utils/message'
import { playSoundNotification } from 'utils/notification'
import { shadeHexColor } from 'utils/styles'

import './style.css'

const ICON_TYPES = {
  SENDER: "SENDER",
  MEGAPHONE: "MEGAPHONE",
  THEME_ICON: "THEME_ICON"
}


const WidgetIcon = () => {
  const [shouldFocusIcon, setShouldFocusIcon] = useState(false)
  const widgetIconRef = useRef(null)

  useForceFocus(widgetIconRef, shouldFocusIcon)

  const { isDisabled, setIsDisabled } = useDisableWidgetIcon()
  const { hoverProps, isHovered } = useOnHover()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const open = useSelector(state =>
    state.view.chatOpen
  )

  const gdpr: GDPRState = useSelector(state =>
    state.session.gdpr
  )

  const widgetIconIsVisible = useSelector(widgetIconVisible)

  const hasActiveConversation = useSelector(state =>
    !!state.conversations.activeConversation
  )

  const widgetAlignment = useWidgetAlignment()

  const activeCampaign = useActiveCampaign()

  const { unreadBadgeEnabled } = useSelector(state =>
    pathOr(false, ['embed', 'configuration', 'theme'], state)
  )

  const unreadMessages = useUnreadMessagesForActiveConversation()

  const isChatTakeover = useSelector(state =>
    state.view.isChatTakeover
  )

  const lastAgentMessage = useLastAgentMessage()
  const isBotMessage = useIsBotMessage(lastAgentMessage)

  const { backgroundStyles, widgetSecondaryColor } = useThemeColors()
  const backgroundColor = useMemo(() =>
    isHovered
      ? shadeHexColor(backgroundStyles.background as string, -35)
      : backgroundStyles.background,
    [isHovered, backgroundStyles]
  )
  const { Icon, customIcon, hasCustomIcon } = useThemeIcon()
  const senderId = useActiveSenderId()
  const { type } = useActiveAnnouncementCampaign()

  const agentAvatarId = senderId || pathOr(undefined, ['authorId'], lastAgentMessage)
  const { recipient } = useRecipientResolution(agentAvatarId)

  const totalUnreadMessages = unreadMessages ? uniq(unreadMessages).length : 0

  const isSlider = type === CampaignDisplayType.SLIDER

  const useAvatarIcon = (agentAvatarId && (isSlider || totalUnreadMessages > 0))

  const [titleNotificationsEnabled = false] = useEmbedThemeProperties(['titleNotificationEnabled'])

  const iconType = useMemo(() => {
    if (useAvatarIcon) {
      return ICON_TYPES.SENDER
    }

    if (isSlider && !agentAvatarId) {
      return ICON_TYPES.MEGAPHONE
    }

    return ICON_TYPES.THEME_ICON
  }, [agentAvatarId, isSlider, useAvatarIcon])

  const widgetIconShape = useWidgetIconShape()

  useEffect(() => {
    if (!titleNotificationsEnabled) {
      return
    }

    broadcast({
      topic: 'page-notification-update',
      message: {
        totalUnreadMessages,
        pageTitleNotificationMsg: t('titleNotifier.newMessageNotification')
      }
    })
  }, [totalUnreadMessages, titleNotificationsEnabled, t])

  useEffect(() => {
    if (isDisabled && !isSlider) {
      setIsDisabled(false)
    }
  }, [isDisabled, isSlider, setIsDisabled])

  useEffect(() => {
    broadcast({
      topic: 'toggle-widget-controller',
      message: {
        isChatTakeover,
        open: open,
        hasActiveConversation: hasActiveConversation && gdpr.hasConsent
      }
    })
  }, [open, gdpr, hasActiveConversation, isChatTakeover])

  useEffect(() => {
    if (unreadMessages && unreadMessages.length > 0) {
      playSoundNotification()
    }
  }, [unreadMessages])

  useEffect(() => {
    listen({
      topic: 'CONDUCTOR:focusWidgetIcon',
      handler: () => setShouldFocusIcon(true)
    })
  }, [])

  const toggle = isOpen => {
    if (isSlider) {
      broadcast({
        topic: 'slider:showDetails',
        message: {}
      })

      setIsDisabled(true)
    } else {
      const gdprDismissed = !gdpr.hasConsent && !gdpr.declinedConsent // is there a decision on gdpr?

      dispatch(toggleChat({
        chatOpen: isOpen,
        gdprDismissed
      }))
    }

    if (isOpen) {
      broadcast({ topic: 'reset-controller-size' })
    }
  }

  const onToggleChat = () => {
    if (!open) {
      if (totalUnreadMessages > 0 && isBotMessage) {
        const playbookId = pathOr(null, ['attributes', 'playbookId'], activeCampaign)

        storeAnnouncementCampaignCompleted(activeCampaign.id, playbookId)
        triggerPlaybookClickedAPIEvent(lastAgentMessage)
      }
    }

    toggle(!open)
  }

  const buttonStyles = useMemo(() => {
    if (useAvatarIcon && !open) {
      return null
    }

    return { background: `${backgroundColor}` }
  }, [useAvatarIcon, open, backgroundColor])

  const iconStyle = useMemo(() => {
    if (!hasCustomIcon || useAvatarIcon || open) {
      return null
    }

    return isHovered ?
      { opacity: 0.65, ...customIcon } :
      customIcon
  }, [customIcon, useAvatarIcon, open, hasCustomIcon, isHovered])


  const buttonAriaLabel = useMemo(() => {
    if (open) {
      return !!recipient.name
        ? `Close chat with ${recipient.name} in the Drift Widget messenger`
        : 'Close Drift Widget messenger'
    }

    const openLabel = (
      !!recipient.name
        ? `Open chat with ${recipient.name} in the Drift Widget messenger`
        : 'Open Drift Widget messenger'
    )

    if (unreadBadgeEnabled && totalUnreadMessages > 0) {
      return `${openLabel} - Unread messages: ${totalUnreadMessages}`
    }

    return openLabel
  }, [open, unreadBadgeEnabled, recipient.name, totalUnreadMessages])

  if (!widgetIconIsVisible) {
    return null
  }

  return (
    <div
      className={classnames(`drift-widget-controller drift-widget-controller--align-${widgetAlignment}`, [widgetIconShape],
        {
          "drift-widget-controller--closed": !isMobileDevice() && open,
          "drift-widget-controller--custom-icon": hasCustomIcon,
          "drift-widget-controller--avatar": useAvatarIcon,
          "drift-widget-controller--hide": isChatTakeover,
          "drift-widget-controller--disabled": isDisabled,
        })}
      onClick={onToggleChat}
      style={buttonStyles}
      {...hoverProps}
      aria-label={buttonAriaLabel}
      aria-disabled={isDisabled}
      aria-hidden={isDisabled}
      role="button"
      tabIndex={!isDisabled ? 0 : -1}
      onKeyDown={onKeyDownSubmit(onToggleChat)}
      ref={widgetIconRef}
    >
      <div
        className={classnames("drift-widget-controller-icon", [widgetIconShape])}
        style={iconStyle}
      >
        <div className="drift-controller-icon--active">
          {iconType === ICON_TYPES.MEGAPHONE &&
            <Megaphone fill={widgetSecondaryColor} />
          }
          {iconType === ICON_TYPES.SENDER &&
            <RecipientAvatar
              className={classnames([widgetIconShape], "drift-controller-icon--avatar")}
              avatarClassName={classnames([widgetIconShape], "drift-controller-icon--avatar-avatar")}
              id={agentAvatarId}
            />
          }
          {iconType === ICON_TYPES.THEME_ICON && !hasCustomIcon && Icon}
        </div>
        <div className="drift-controller-icon--close" style={{ backgroundColor: widgetSecondaryColor }}></div>
      </div>
      {unreadBadgeEnabled && totalUnreadMessages > 0 &&
        <div className="drift-controller-icon-unread">
          {Math.min(totalUnreadMessages, 99)}
        </div>
      }
    </div>
  )
}

export default WidgetIcon
